import React from 'react';
import { Navigate } from 'react-router-dom';

import { UserRolesIdsEnum } from '@verantos-dev/core-enums';

import useAuth from './useAuth';

export const withAuth =
  <P extends object>(
    Component: React.ComponentType<P>,
    appId?: string,
    claimCheckDefinitionMap?: Record<string, UserRolesIdsEnum | UserRolesIdsEnum[]>,
    fallbackRoute = '/',
    replaceOnRedirect = false
  ): React.FC<P> =>
  (props: P) => {
    const {
      user: { roles },
    } = useAuth();

    // Opt-in path: when a route maps its appId to an ARRAY of roles, grant access
    // if the user has ANY of them (OR-match), and redirect via the configurable
    // fallbackRoute/replaceOnRedirect. This is only reached by consumers that pass
    // array values (annotation-tool). Single-role consumers (VEP / internal-platform)
    // pass an enum value, skip this block entirely, and hit the unchanged logic below.
    const required = appId ? claimCheckDefinitionMap?.[appId] : undefined;
    if (Array.isArray(required)) {
      const userRoleIds = roles.map(({ roleId }) => roleId);
      return required.some((role) => userRoleIds.includes(role)) ? (
        <Component {...props} appId={appId} />
      ) : (
        <Navigate to={fallbackRoute} replace={replaceOnRedirect} />
      );
    }

    // --- Original core-auth logic, unchanged from main. The `as` narrowing only
    // exists because the map type widened above; arrays are already handled, so
    // here the value is always a single role. ---
    if (
      appId === undefined ||
      (appId &&
        claimCheckDefinitionMap &&
        roles
          .map(({ roleId }) => roleId)
          .includes(claimCheckDefinitionMap[appId] as UserRolesIdsEnum))
    ) {
      return <Component {...props} appId={appId} />;
    }

    return <Navigate to="/" />;
  };
